import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { ApiService } from '../../../core/services/api.service';
import { AuthService } from '../../../core/services/auth.service';
import { CenterService } from '../../../core/services/center.service';
import { VioTablesV2Module, VT2_TABLE_CONFIG } from 'vio-tables-v2';
import { MEMBER_TABLE_BASE } from '../../../pages/operator/table-configs';
import { BookExchangeComponent } from '../../../pages/operator/reports/book-exchange.component';
import { DateFormatService } from '../../../core/services/date-format.service';
import { AppDatePipe } from '../../../core/pipes/app-date.pipe';

@Component({
  selector: 'app-member-list-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, VioTablesV2Module, BookExchangeComponent, AppDatePipe],
  templateUrl: './member-list-panel.component.html',
  styleUrl: './member-list-panel.component.scss',
})
export class MemberListPanelComponent implements OnInit {
  /** If set, loads via getStatDetail(); otherwise loads all members. */
  @Input() statFilter?: string;
  /** Optional memberType filter ('H' = Door Delivery or 'A' = At Center) for stat detail queries. */
  @Input() statMemberType?: string;
  /** Optional locationId filter to scope results to a specific route/center. */
  @Input() locationId?: string;
  /** Show the "Add New Member" button in the header. */
  @Input() showAddButton = false;

  @Output() addRequested = new EventEmitter<void>();
  @Output() editRequested = new EventEmitter<any>();

  @ViewChild('customDateInput') customDateInput?: ElementRef<HTMLInputElement>;

  // List
  records: any[] = [];
  isLoading = false;

  /** Closure Reason column text for members closed before reasons existed. */
  readonly NOT_RECORDED_REASON = 'Not recorded';

  // Refresh membership status
  isRefreshing = false;
  refreshMsg = '';
  refreshError = '';

  // History
  showHistory = false;
  historyTitle = '';
  historyLoading = false;
  historyData: any[] = [];
  /** Member whose history is open — used for the closure banner. */
  historyMember: any = null;

  // Transaction report
  showTransactionReport = false;
  transactionReportMember: any = null;

  // Membership management popup (💳 button)
  showMembership = false;
  membershipRecord: any = null;
  activeMmpSection: 'renew' | 'hold' | 'close' | '' = '';
  showRenewalConfirm = false;
  confirmNewExpiryLabel = '';
  confirmPeriodLabel = '';
  membershipMonths = 12;
  membershipAmount: number | null = null;
  membershipSaving = false;
  membershipSuccessMsg = '';
  membershipErrorMsg = '';
  subscriptionPlans: any[] = [];
  isLoadingPlans = false;
  renewStartOption: 'expiry' | 'today' | 'custom' = 'expiry';
  renewCustomDate = '';
  renewPaymentMode = 'Cash';
  renewPaymentTrnxRef = '';
  renewPaymentRemarks = '';

  // Hold section
  mmpHoldTill = '';

  // Close section (new unified form)
  mmpCloseRefundMode = 'Cash';
  mmpCloseRefundTrnxId = '';
  mmpCloseRefundAmount: number | null = null;
  mmpCloseReturnedTo = '';
  mmpCloseReturnedToPhone = '';
  mmpCloseRefundDate = '';
  mmpCloseRemarks = '';
  /** _id of the selected closure reason (the server stores its id + text). */
  mmpCloseReasonId = '';
  /** Active canned reasons for the closure dropdown (Management → Closure Reasons). */
  closureReasons: any[] = [];
  closureReasonsLoading = false;
  // Inline "+" add-reason popup beside the dropdown
  showAddReason = false;
  newReasonName = '';
  addReasonSaving = false;
  addReasonError = '';

  readonly paymentModes = ['UPI', 'Cash', 'Chq', 'Bank Transfer', 'Other'];

  get isAdmin(): boolean { return this.auth.getCurrentUser()?.role?.toLowerCase() === 'admin'; }
  get isManager(): boolean { return this.auth.getCurrentUser()?.role?.toLowerCase() === 'manager'; }
  get selectedCenter() { return this.centerSvc.selectedCenter; }

  tableConfig: VT2_TABLE_CONFIG = {
    ...MEMBER_TABLE_BASE,
    excludeFields: [...(MEMBER_TABLE_BASE.excludeFields ?? []), '_memberTypeRaw'],
    maxActionButtons: 5,
    actionButtons: [
      { icon: '✏', title: 'Edit Details', cssClass: 'vt2-icon-btn--edit', onClick: (row: any) => this.editRequested.emit(row) },
      { icon: '💳', title: 'Membership',   cssClass: '', onClick: (row: any) => this.openMembershipManagement(row) },
      { icon: '📋', title: 'Books',        cssClass: '', onClick: (row: any) => this.openHistory(row) },
      { icon: '📊', title: 'Book Exchange History', cssClass: '', onClick: (row: any) => this.openTransactionReport(row) },
    ],
  };

  constructor(
    private api: ApiService,
    private auth: AuthService,
    private centerSvc: CenterService,
    private dateFmt: DateFormatService,
  ) {}

  ngOnInit(): void {
    this.tableConfig = { ...this.tableConfig, dateDisplayFormat: this.dateFmt.current };
    this.loadData();
  }

  loadData(): void {
    this.isLoading = true;
    if (this.statFilter) {
      const extra: Record<string, any> = {};
      if (this.statMemberType) extra['memberType'] = this.statMemberType;
      if (this.locationId)     extra['locationId'] = this.locationId;
      this.api.getStatDetail(this.statFilter, extra).subscribe({
        next: (data) => {
          this.setRecords((data || [])
            .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''))
            .map((m: any) => this.mapMember(m, null, null)));
          this.isLoading = false;
        },
        error: () => { this.isLoading = false; },
      });
    } else {
      forkJoin({ routes: this.api.getRouteMasters(), centers: this.api.getCenters(), members: this.api.getMembers() }).subscribe({
        next: ({ routes, centers, members }) => {
          let memberList: any[] = members || [];
          if (this.isManager) {
            const area = this.centerSvc.selectedCenter?.area?.trim().toLowerCase();
            if (area) memberList = memberList.filter((m: any) => (m.area || '').trim().toLowerCase() === area);
          }
          this.setRecords(memberList
            .sort((a: any, b: any) => (a.name || '').localeCompare(b.name || ''))
            .map((m: any) => this.mapMember(m, routes || [], centers || [])));
          this.isLoading = false;
        },
        error: () => { this.isLoading = false; },
      });
    }
  }

  private setRecords(rows: any[]): void {
    this.records = rows;
  }

  private closureReasonOf(r: any): string {
    return (r.closureDetails?.closureReason || '').trim() || this.NOT_RECORDED_REASON;
  }

  private _canonicalStatus(raw: string): string {
    if (!raw) return raw;
    const s = raw.trim().toUpperCase();
    if (s === 'MEMBER')                          return 'MEMBER';
    if (s === 'ONHOLD' || s === 'HOLD')          return 'OnHold';
    if (s === 'DUE'    || s === 'EXPIRY')        return 'Due';
    if (s === 'CLOSED' || s === 'CLOSE' || s === 'CLOCE') return 'Closed';
    return raw;
  }

  private mapMember(m: any, routes: any[] | null, centers: any[] | null): any {
    const locationName = (routes || centers)
      ? (routes?.find((r: any) => r._id === m.locationId)?.description
          || centers?.find((c: any) => c._id === m.locationId)?.centerName
          || '—')
      : (m.routeDisplayId || '—');
    const status = this._canonicalStatus(m.status);
    const row: any = {
      name: m.name,
      memNo: m.memNo,
      phone: m.phone,
      ...m,
      _memberTypeRaw: m.memberType,
      memberType: m.memberType === 'H' ? 'Door Delivery' : 'At Center',
      status,
      routeDesc: locationName,
    };
    // vio-table derives column order from the first row's keys: Name, then
    // Member Status (quick view of membership state), then the rest, with
    // Closure Reason last (set on every row, blank for members not closed).
    const ordered: any = { name: row.name, status };
    for (const k of Object.keys(row)) {
      if (!(k in ordered)) ordered[k] = row[k];
    }
    ordered.closureReason = status === 'Closed' ? this.closureReasonOf(m) : '';
    return ordered;
  }

  // ── History ──────────────────────────────────────────────────────────────────

  openHistory(record: any): void {
    this.historyTitle = record.name;
    this.historyMember = record;
    this.historyData = [];
    this.showHistory = true;
    this.historyLoading = true;
    this.api.getMemberTransactionHistory(record._id).subscribe({
      next: (data) => { this.historyData = data || []; this.historyLoading = false; },
      error: () => { this.historyLoading = false; },
    });
  }

  closeHistory(): void { this.showHistory = false; }

  // ── Transaction report ───────────────────────────────────────────────────────

  openTransactionReport(record: any): void {
    this.transactionReportMember = record;
    this.showTransactionReport = true;
  }

  closeTransactionReport(): void {
    this.showTransactionReport = false;
    this.transactionReportMember = null;
  }

  // ── Membership management ────────────────────────────────────────────────────

  openMembershipManagement(record: any): void {
    this.membershipRecord = record;
    this.membershipSuccessMsg = '';
    this.membershipErrorMsg = '';
    this.activeMmpSection = '';
    this.showRenewalConfirm = false;
    this.renewStartOption = record?.membershipExpiresOn ? 'expiry' : 'today';
    this.renewCustomDate = this.todayString();
    this.renewPaymentMode = 'Cash';
    this.renewPaymentTrnxRef = '';
    this.renewPaymentRemarks = '';
    // Hold section
    this.mmpHoldTill = '';
    // Close section
    this.mmpCloseRefundMode = 'Cash';
    this.mmpCloseRefundTrnxId = '';
    this.mmpCloseRefundAmount = null;
    this.mmpCloseReturnedTo = '';
    this.mmpCloseReturnedToPhone = '';
    this.mmpCloseRefundDate = this.todayString();
    this.mmpCloseRemarks = '';
    this.mmpCloseReasonId = '';
    this.showAddReason = false;
    this.showMembership = true;
    this.isLoadingPlans = true;
    this.subscriptionPlans = [];
    this.api.getSubscriptionPlans().subscribe({
      next: (plans) => {
        this.subscriptionPlans = plans || [];
        const defaultPlan = this.subscriptionPlans.find(p => p.months === 12) || this.subscriptionPlans[0];
        this.membershipMonths = defaultPlan?.months ?? 12;
        this.membershipAmount = defaultPlan?.charges ?? null;
        this.isLoadingPlans = false;
      },
      error: () => {
        this.subscriptionPlans = [];
        this.membershipMonths = 12;
        this.membershipAmount = null;
        this.isLoadingPlans = false;
      },
    });
  }

  toggleMmpSection(section: 'renew' | 'hold' | 'close'): void {
    this.membershipSuccessMsg = '';
    this.membershipErrorMsg = '';
    this.showRenewalConfirm = false;
    this.activeMmpSection = this.activeMmpSection === section ? '' : section;
    if (this.activeMmpSection === 'close') this.loadClosureReasons();
  }

  private loadClosureReasons(selectId?: string): void {
    this.closureReasonsLoading = true;
    this.api.getClosureReasons({ isActive: 'true' }).subscribe({
      next: (reasons) => {
        this.closureReasons = reasons || [];
        this.closureReasonsLoading = false;
        if (selectId) this.mmpCloseReasonId = selectId;
      },
      error: () => {
        this.closureReasons = [];
        this.closureReasonsLoading = false;
      },
    });
  }

  mmpShowSection(key: 'renew' | 'hold' | 'close'): boolean {
    const s = (this.membershipRecord?.status || '').toUpperCase();
    if (key === 'renew') return s === 'MEMBER' || s === 'DUE' || s === 'ONHOLD' || s === 'CLOSED';
    if (key === 'hold')  return s === 'MEMBER' || s === 'DUE';
    if (key === 'close') return s === 'MEMBER' || s === 'ONHOLD' || s === 'DUE';
    return false;
  }

  confirmOnHold(): void {
    if (!this.mmpHoldTill) { this.membershipErrorMsg = 'Please select a date.'; return; }
    this.membershipSaving = true;
    this.membershipErrorMsg = '';
    this.api.setMemberStatus(this.membershipRecord._id, 'OnHold', this.mmpHoldTill).subscribe({
      next: () => {
        this.membershipSaving = false;
        this.membershipSuccessMsg = 'Membership put on hold successfully.';
        this.loadData();
      },
      error: (err: any) => {
        this.membershipSaving = false;
        this.membershipErrorMsg = err?.message || 'Failed to update status.';
      },
    });
  }


  openAddReason(): void {
    this.newReasonName = '';
    this.addReasonError = '';
    this.showAddReason = true;
  }

  cancelAddReason(): void {
    this.showAddReason = false;
    this.addReasonError = '';
  }

  /** Saves a new canned reason from the closure panel, then selects it. */
  saveNewReason(): void {
    const name = this.newReasonName.trim();
    if (name.length < 2) {
      this.addReasonError = 'Enter at least 2 characters.';
      return;
    }
    this.addReasonSaving = true;
    this.addReasonError = '';
    this.api.saveClosureReason({ reasonName: name, isActive: true }).subscribe({
      next: (res: any) => {
        this.addReasonSaving = false;
        this.showAddReason = false;
        this.loadClosureReasons(res?.insertedId ? String(res.insertedId) : undefined);
      },
      error: (err: any) => {
        this.addReasonSaving = false;
        this.addReasonError = err?.error?.message || err?.message || 'Failed to add reason.';
      },
    });
  }

  confirmClose(): void {
    this.membershipErrorMsg = '';
    // A reason is mandatory once any have been set up; with none configured,
    // closure still works as before (reason left blank).
    if (this.closureReasons.length && !this.mmpCloseReasonId) {
      this.membershipErrorMsg = 'Please select a reason for closing the membership.';
      return;
    }
    this.membershipSaving = true;
    this.api.closeMembership(this.membershipRecord._id, {
      refundMode:      this.mmpCloseRefundMode,
      refundTrnxId:    this.mmpCloseRefundTrnxId,
      refundAmount:    this.mmpCloseRefundAmount ?? undefined,
      returnedTo:      this.mmpCloseReturnedTo,
      returnedToPhone: this.mmpCloseReturnedToPhone,
      refundDate:      this.mmpCloseRefundDate || this.todayString(),
      refundRemarks:   this.mmpCloseRemarks,
      closureReasonId: this.mmpCloseReasonId || undefined,
    }).subscribe({
      next: () => {
        this.membershipSaving = false;
        this.membershipSuccessMsg = 'Membership closed successfully.';
        this.loadData();
      },
      error: (err: any) => {
        this.membershipSaving = false;
        this.membershipErrorMsg = err?.message || 'Failed to close membership.';
      },
    });
  }

  closeMembershipManagement(): void {
    this.showMembership = false;
    this.membershipRecord = null;
  }

  get hasExpiryDate(): boolean { return !!this.membershipRecord?.membershipExpiresOn; }

  getMemberDisplayStatus(record: any): { label: string; cls: string } {
    const st = (record?.status || '').toUpperCase();
    if (st === 'CLOSED') return { label: 'Closed',  cls: 'bg-danger' };
    if (st === 'ONHOLD') return { label: 'On Hold', cls: 'bg-warning text-dark' };
    if (st === 'DUE')    return { label: 'Due',     cls: 'bg-danger' };
    if (record?.membershipExpiresOn) {
      const days = Math.floor((new Date(record.membershipExpiresOn).getTime() - Date.now()) / 86400000);
      if (days < 0)   return { label: 'Due',      cls: 'bg-danger' };
      if (days <= 30) return { label: 'Expiring', cls: 'bg-warning text-dark' };
    }
    return { label: 'Active', cls: 'bg-success' };
  }

  doRefreshMembershipStatus(): void {
    this.isRefreshing = true;
    this.refreshMsg = '';
    this.refreshError = '';
    this.api.refreshMembershipStatus().subscribe({
      next: (res) => {
        this.isRefreshing = false;
        const member = res?.movedToMember ?? 0;
        const due    = res?.movedToDue    ?? 0;
        const hold   = res?.movedToHold   ?? 0;
        if (member === 0 && due === 0 && hold === 0) {
          this.refreshMsg = 'All memberships are up to date.';
        } else {
          const parts: string[] = [];
          if (member > 0) parts.push(`${member} restored to Member`);
          if (due    > 0) parts.push(`${due} moved to Due`);
          if (hold   > 0) parts.push(`${hold} moved to On Hold`);
          this.refreshMsg = parts.join(', ') + '.';
        }
        this.loadData();
      },
      error: (err: any) => {
        this.isRefreshing = false;
        this.refreshError = err?.message || 'Failed to refresh membership status.';
      },
    });
  }

  getRenewalStartDate(): Date {
    let dateStr: string;
    if (this.renewStartOption === 'expiry') {
      const exp = this.membershipRecord?.membershipExpiresOn;
      dateStr = exp ? (exp as string).split('T')[0] : this.todayString();
    } else if (this.renewStartOption === 'today') {
      dateStr = this.todayString();
    } else {
      dateStr = this.renewCustomDate || this.todayString();
    }
    const [y, mo, d] = dateStr.split('-').map(Number);
    return new Date(y, mo - 1, d);
  }

  getRenewalStartDateStr(): string {
    if (this.renewStartOption === 'expiry') {
      const exp = this.membershipRecord?.membershipExpiresOn;
      return exp ? (exp as string).split('T')[0] : this.todayString();
    }
    if (this.renewStartOption === 'today') return this.todayString();
    return this.renewCustomDate || this.todayString();
  }

  isMembershipExpired(): boolean {
    if (!this.membershipRecord?.membershipExpiresOn) return false;
    return new Date(this.membershipRecord.membershipExpiresOn) < new Date();
  }

  getNewExpiryDate(): Date {
    const start = new Date(this.getRenewalStartDate());
    start.setMonth(start.getMonth() + (this.membershipMonths || 12));
    return start;
  }

  onPlanSelected(): void {
    const plan = this.subscriptionPlans.find(p => p.months === Number(this.membershipMonths));
    this.membershipAmount = plan?.charges ?? null;
  }

  previewRenewal(): void {
    if (!this.membershipRecord) return;
    if (this.renewStartOption === 'custom' && !this.renewCustomDate) {
      this.membershipErrorMsg = 'Please select a start date.';
      return;
    }
    this.membershipErrorMsg = '';
    this.confirmNewExpiryLabel = this.dateFmt.format(this.getNewExpiryDate());
    const plan = this.subscriptionPlans.find(p => p.months === Number(this.membershipMonths));
    this.confirmPeriodLabel = plan?.label || `${this.membershipMonths} Month(s)`;
    this.showRenewalConfirm = true;
  }

  cancelRenewalConfirm(): void {
    this.showRenewalConfirm = false;
  }

  confirmRenewal(): void {
    if (!this.membershipRecord) return;
    this.membershipSaving = true;
    this.membershipSuccessMsg = '';
    this.membershipErrorMsg = '';
    const startDate = this.getRenewalStartDateStr();
    this.api.renewMembership(
      this.membershipRecord._id, this.membershipMonths, this.membershipAmount ?? undefined, startDate,
      { paymentMode: this.renewPaymentMode, paymentTrnxRef: this.renewPaymentTrnxRef, paymentRemarks: this.renewPaymentRemarks }
    ).subscribe({
      next: () => {
        this.membershipSaving = false;
        this.showRenewalConfirm = false;
        this.membershipSuccessMsg = `Membership renewed! New expiry: ${this.confirmNewExpiryLabel}.`;
        this.loadData();
      },
      error: (err: any) => {
        this.membershipSaving = false;
        this.showRenewalConfirm = false;
        this.membershipErrorMsg = err?.message || 'Failed to renew membership.';
      },
    });
  }

  // ── Utilities ────────────────────────────────────────────────────────────────

  todayString(): string {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }

  openCustomDatePicker(): void {
    this.renewStartOption = 'custom';
    const el = this.customDateInput?.nativeElement;
    if (!el) return;
    try {
      if (typeof (el as any).showPicker === 'function') (el as any).showPicker();
      else el.click();
    } catch { el.focus(); }
  }

}
